import { useMemo, useState } from "react";
import { CreativeElementDto } from "@repo/api/creative-element";
import { Checkbox } from "@/lib/components/Checkbox";
import { Button } from "@/lib/components/Button/Button";
import Image from "@/lib/components/Image";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { IconClock, IconDots, IconTrash } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { useDeleteCreativeElementMutation } from "@/lib/queries/creative-element-queries";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { type CreativeElementCategory } from "@repo/api/creative-element";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/lib/components/Tooltip";

type Props = {
  element: CreativeElementDto;
  isSelected: boolean;
  isInVideo: boolean;
  onSelect: () => void;
  activeTab: CreativeElementCategory;
};

const HUMAN_CARD_ASPECT_RATIO = 9 / 16;
const DEFAULT_CARD_ASPECT_RATIO = 3 / 2;
const THRESHOLD = 0.3;

export default function CreativeElementsListItem({
  element,
  isSelected,
  isInVideo,
  onSelect,
  activeTab,
}: Props) {
  const isTeamElement = element.teamExternalId !== null;
  const isHumanTab = activeTab === "HUMAN";

  const cardAspectRatio = isHumanTab
    ? HUMAN_CARD_ASPECT_RATIO
    : DEFAULT_CARD_ASPECT_RATIO;

  const objectFit = useMemo(() => {
    const { width, height } = element;
    if (!width || !height) return "object-contain";

    const imageAspectRatio = width / height;
    const ratio = imageAspectRatio / cardAspectRatio;

    if (ratio >= 1 - THRESHOLD && ratio <= 1 + THRESHOLD) {
      return "object-cover";
    }
    return "object-contain";
  }, [element, cardAspectRatio]);

  return (
    <div
      onClick={onSelect}
      className={cn(
        "group/item cursor-pointer p-0 focus-visible:outline-2",
        isSelected
          ? "rounded-[10px] border-2 border-transparent"
          : "m-px rounded-[9px] border",
      )}
      style={
        isSelected
          ? {
              background:
                "linear-gradient(white, white) padding-box, linear-gradient(to bottom right, var(--color-primary-400), var(--color-secondary-400)) border-box",
            }
          : undefined
      }
    >
      <div
        className={cn(
          "relative rounded-md",
          isHumanTab ? "aspect-9/16" : "aspect-3/2",
        )}
      >
        <Checkbox checked={isSelected} className="absolute top-3 left-3 z-10" />
        {isTeamElement && (
          <div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity group-hover/item:opacity-100">
            <ElementMenu elementExternalId={element.externalId} />
          </div>
        )}
        {isInVideo && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="absolute bottom-2 left-2 z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-white">
                <IconClock size={16} className="text-tertiary-400" />
              </div>
            </TooltipTrigger>
            <TooltipContent>Used in this video</TooltipContent>
          </Tooltip>
        )}
        <div
          className="h-full w-full rounded-md"
          style={{
            backgroundImage:
              "linear-gradient(45deg, #F7F7F7 25%, transparent 25%), linear-gradient(-45deg, #F7F7F7 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F7F7F7 75%), linear-gradient(-45deg, transparent 75%, #F7F7F7 75%)",
            backgroundSize: "16px 16px",
            backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
          }}
        >
          <Image
            src={element.thumbnailSrc}
            className={cn("h-full w-full rounded-md", objectFit)}
          />
        </div>
      </div>
    </div>
  );
}

function ElementMenu({ elementExternalId }: { elementExternalId: string }) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const teamExternalId = useEditorStore((state) => state.teamExternalId);

  const { mutate: deleteElement, isPending: isDeleting } =
    useDeleteCreativeElementMutation({
      teamExternalId,
      onSuccess: () => setDeleteDialogOpen(false),
    });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="iconXxs"
            onClick={(e) => e.stopPropagation()}
          >
            <IconDots size={16} className="text-text-primary" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuItem
            className="text-sm font-medium text-error"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialogOpen(true);
            }}
          >
            <IconTrash size={18} />
            Delete element
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent
          className="max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader className="w-full">
            <DialogTitle>Delete Element</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this element? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => deleteElement(elementExternalId)}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isDeleting}>
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
